import { useMemo } from 'react';
import { useVisit } from '../VisitContext';
import { VisitInterventionTask } from './VisitInterventionTask';
import { VisitBucketItemWrapper } from '../shared/VisitBucketItemWrapper';
import { FormRegistryProvider } from '~/shared/forms/FormRegistryContext';
import { VisitTaskFooter } from '../shared/VisitTaskFooter';
import { resolveInterventionTasksOptions } from '@generated';
import { convertToServerValues } from './values';
import { ExternalReferenceValueCopier } from '../shared/ExternalReferenceValueCopier';

import { VisitBucketItemBlocker } from '../shared/VisitBucketItemBlocker';
import { useGetVisitBucketItemOptions, useGetVisitStatusOptions } from '../queries';
import { useQuery } from '@tanstack/react-query';

export function VisitInterventionTasks() {
	const { id, selectedVisitBucket, selectedVisitBucketItem, navigateToNextItem } = useVisit();
	const { refetch: refetchVisitStatus } = useQuery(useGetVisitStatusOptions(id));
	const byCategory = Boolean(selectedVisitBucketItem?.category);
	const { refetch: refetchVisitItem } = useQuery(
		useGetVisitBucketItemOptions(
			id,
			selectedVisitBucket.id,
			byCategory ? selectedVisitBucketItem!.category!.name : selectedVisitBucketItem!.interventionTask!.id,
			byCategory ? 'intervention-category' : 'intervention'
		)
	);

	const selectedTasks = useMemo(
		() =>
			selectedVisitBucketItem?.category
				? selectedVisitBucketItem.category.interventionTasks
				: selectedVisitBucketItem?.interventionTask
				? [selectedVisitBucketItem.interventionTask]
				: [],
		[selectedVisitBucketItem]
	);

	if (!selectedVisitBucketItem) {
		throw new Error('No bucket item selected');
	} else if (selectedTasks.length === 0) {
		throw new Error('Selected bucket item does not have any associated tasks');
	}

	return (
		<FormRegistryProvider
			saveMutation={resolveInterventionTasksOptions}
			onSaveTransform={values => convertToServerValues(selectedTasks, values)}
			onSuccess={async () => {
				await refetchVisitItem();
				refetchVisitStatus();
				navigateToNextItem?.();
			}}
		>
			<VisitBucketItemWrapper footerSlot={<VisitTaskFooter />}>
				<div className="space-y-4">
					{selectedTasks.map(task => (
						<VisitInterventionTask
							key={task.id}
							interventionTask={task}
							siblingInterventionTasks={selectedTasks.filter(x => x.id !== task.id)}
						/>
					))}
				</div>
			</VisitBucketItemWrapper>
			<VisitBucketItemBlocker />
			{selectedVisitBucketItem.category?.name !== 'custom' && (
				<ExternalReferenceValueCopier
					selectedTasks={selectedTasks.map(x => ({
						id: x.id,
						externalReferences: x.interventionDefinition ? x.interventionDefinition.externalReferences : [],
						documentationFormFields: x.interventionDefinition ? x.interventionDefinition.documentationFormFields : []
					}))}
				/>
			)}
		</FormRegistryProvider>
	);
}
